/**
 * Backend User-Flow Audit dashboard — read-only viewer over static audit-data.json.
 */
import { useMemo, useState, type ReactNode } from "react"
import {
  Activity,
  Wrench,
  Clock,
  Cpu,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Database,
  Zap,
  GitCompare,
} from "lucide-react"
import auditData from "./audit-data.json"
import { cn } from "@/lib/utils"

// Loose types — the JSON is captured data, not a hand-authored contract.
type AnyObj = Record<string, any>
const DATA: AnyObj = auditData as AnyObj
const RUNS: AnyObj[] = DATA.runs || []
const FINDINGS: AnyObj[] = DATA.findings || []

const FLOW_ORDER = ["generation", "edit", "chat", "goals"]
const FLOW_LABEL: Record<string, string> = {
  generation: "Generation Lifecycle",
  edit: "Edit Lifecycle",
  chat: "Chat Flows",
  goals: "Goals (REST)",
}
const LEVEL_COLOR: Record<string, string> = {
  L1: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  L2: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  L3: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  L4: "bg-rose-500/15 text-rose-600 border-rose-500/30",
}
const SEV_COLOR: Record<string, string> = {
  CRITICAL: "bg-rose-500/15 text-rose-600 border-rose-500/40",
  MAJOR: "bg-amber-500/15 text-amber-600 border-amber-500/40",
  MINOR: "bg-sky-500/15 text-sky-600 border-sky-500/40",
  INFO: "bg-muted text-muted-foreground border-border",
}

const STATUS_COLOR: Record<string, string> = {
  resolved: "bg-emerald-500/15 text-emerald-600 border-emerald-500/40",
  verified: "bg-emerald-500/15 text-emerald-600 border-emerald-500/40",
  improved: "bg-teal-500/15 text-teal-600 border-teal-500/40",
  open: "bg-rose-500/10 text-rose-600 border-rose-500/30",
  info: "bg-muted text-muted-foreground border-border",
}
const STATUS_LABEL: Record<string, string> = {
  resolved: "RESOLVED",
  verified: "VERIFIED",
  improved: "IMPROVED",
  open: "OPEN",
  info: "INFO",
}

function fmtSecs(s: number | undefined) {
  if (s == null) return "-"
  if (s < 1) return `${Math.round(s * 1000)}ms`
  return `${s.toFixed(1)}s`
}
function fmtMs(ms: number | null | undefined) {
  if (ms == null) return "-"
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`
  return `${Math.round(ms)}ms`
}
function fmtTok(n: number | undefined) {
  if (!n) return "0"
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return `${n}`
}

function Kpi({ title, value, icon: Icon, sub }: { title: string; value: string | number; icon: any; sub?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{title}</span>
        <Icon className="size-4 text-muted-foreground" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  )
}

function StatusBadge({ status }: { status: number }) {
  const ok = status >= 200 && status < 300
  const warn = status >= 400 && status < 500
  return (
    <span
      className={cn(
        "inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-mono",
        ok && "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
        warn && "bg-amber-500/15 text-amber-600 border-amber-500/30",
        !ok && !warn && "bg-rose-500/15 text-rose-600 border-rose-500/30"
      )}
    >
      {status}
    </span>
  )
}

function JsonBlock({ value }: { value: any }) {
  return (
    <pre className="max-h-72 overflow-auto rounded bg-muted/50 p-2 text-xs font-mono whitespace-pre-wrap break-all">
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  )
}

function Section({ title, subtitle, children }: { title: ReactNode; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-sm font-semibold mb-2 flex items-center justify-between gap-2">
        <span>{title}</span>
        {subtitle ? <span className="text-xs font-normal text-muted-foreground">{subtitle}</span> : null}
      </div>
      {children}
    </div>
  )
}

function Collapse({ title, defaultOpen = false, children }: { title: ReactNode; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded border">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-sm font-medium hover:bg-muted/50"
      >
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        {title}
      </button>
      {open && <div className="border-t p-2">{children}</div>}
    </div>
  )
}

function cfgStr(c: AnyObj | undefined) {
  if (!c) return ""
  const model = (c.model || "").replace("claude-", "")
  const think = c.thinking === "disabled" ? "no-think" : `think ${fmtTok(c.thinking_budget)}`
  return `${model} · ${think} · max ${fmtTok(c.max_tokens)}`
}

function TraceTimeline({ trace }: { trace: AnyObj[] }) {
  const evs = (trace || []).filter((e) => e.kind === "llm_turn" || e.kind === "tool").sort((a, b) => a.ts - b.ts)
  if (!evs.length) return <p className="text-sm text-muted-foreground">No agent activity (deterministic gate / no LLM, or single-shot fallback).</p>
  const t0 = Math.min(...evs.map((e) => e.ts))
  let prevEnd = t0
  return (
    <div className="space-y-1">
      {evs.map((e, i) => {
        const at = (e.ts - t0).toFixed(1)
        const gap = e.ts - prevEnd // seconds idle/overhead before this event
        prevEnd = e.ts + (e.duration_ms || 0) / 1000
        const gapBadge =
          gap > 1 ? (
            <span className="ml-1 rounded bg-amber-500/15 text-amber-600 px-1 text-[10px]">+{gap.toFixed(1)}s gap</span>
          ) : null
        if (e.kind === "llm_turn") {
          const u = e.usage || {}
          const slow = (e.duration_ms || 0) > 60000
          return (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className="w-12 shrink-0 font-mono text-muted-foreground">+{at}s</span>
              <Cpu className="size-3.5 shrink-0 text-sky-600 mt-0.5" />
              <div className="flex-1">
                <span className="font-medium">LLM turn {e.iteration}</span>{" "}
                <span className={cn("font-mono", slow ? "text-rose-600 font-semibold" : "text-muted-foreground")}>{fmtMs(e.duration_ms)}</span>
                {gapBadge}
                <span className="text-muted-foreground">
                  {" "}· {e.stop_reason} · in {fmtTok(u.input_tokens)} / out {fmtTok(u.output_tokens)}
                  {u.cache_read_input_tokens ? ` · cache-read ${fmtTok(u.cache_read_input_tokens)}` : ""}
                  {u.cache_creation_input_tokens ? ` · cache-write ${fmtTok(u.cache_creation_input_tokens)}` : ""}
                  {e.ttft_ms != null ? ` · TTFT ${fmtMs(e.ttft_ms)}` : ""}
                </span>
                {e.config && <div className="text-[10px] text-muted-foreground font-mono">⚙ {cfgStr(e.config)}</div>}
                {e.thinking && (
                  <Collapse title={<span className="text-violet-600">🧠 extended thinking ({(e.thinking.length / 1000).toFixed(1)}k chars)</span>}>
                    <JsonBlock value={e.thinking} />
                  </Collapse>
                )}
              </div>
            </div>
          )
        }
        return (
          <div key={i} className="text-xs">
            <div className="flex items-start gap-2">
              <span className="w-12 shrink-0 font-mono text-muted-foreground">+{at}s</span>
              <Wrench className="size-3.5 shrink-0 text-violet-600 mt-0.5" />
              <div className="flex-1">
                <span className="font-mono font-medium">{e.name}</span>{" "}
                <span className="text-muted-foreground">{fmtMs(e.duration_ms)}</span>
                {gapBadge}
                {e.error && <span className="ml-1 text-rose-600">· {e.error}</span>}
                <Collapse title={<span className="text-muted-foreground">input / output</span>}>
                  <div className="space-y-1">
                    <div className="text-muted-foreground">input:</div>
                    <JsonBlock value={e.tool_input} />
                    <div className="text-muted-foreground">output:</div>
                    <JsonBlock value={e.result} />
                  </div>
                </Collapse>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ExerciseList({ exercises }: { exercises: AnyObj[] }) {
  return (
    <ul className="ml-4 mt-0.5 space-y-0.5">
      {exercises.map((ex, i) => (
        <li key={i} className="text-xs">
          <span className="font-medium">{ex.exercise}</span>
          {ex.sets != null && ex.reps != null && (
            <span className="text-muted-foreground">
              {" "}
              — {ex.sets}×{ex.reps}
              {ex.weight ? ` @ ${ex.weight}` : ""}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

function DayRow({ date, day }: { date: string; day: AnyObj }) {
  const isRest = day.rest_day || (!day.exercises && !day.sessions)
  const sessions: AnyObj[] = day.sessions || (day.exercises ? [{ exercises: day.exercises }] : [])
  return (
    <div className="border-t py-1.5 first:border-t-0">
      <div className="text-xs font-semibold">
        {date} · {day.day_name}
        {day.name ? ` — ${day.name}` : isRest ? " — Rest day" : ""}
        {day.category ? <span className="ml-1 font-normal text-muted-foreground">({day.category})</span> : null}
      </div>
      {day.goal && <div className="text-xs text-muted-foreground">{day.goal}</div>}
      {sessions.map((s, i) => (
        <div key={i}>
          {s.sport_name && <div className="text-xs italic text-muted-foreground ml-4">{s.sport_name}</div>}
          {s.exercises && s.exercises.length > 0 && <ExerciseList exercises={s.exercises} />}
        </div>
      ))}
    </div>
  )
}

function GeneratedOutput({ trace }: { trace: AnyObj[] }) {
  const outs = (trace || []).filter((e) => e.kind === "generation_output")
  if (!outs.length) return null
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-sm font-semibold mb-2">
        Final generated output — {outs.length} week{outs.length > 1 ? "s" : ""} the agent produced
      </div>
      <div className="space-y-3">
        {outs.map((o, i) => {
          const week = o.week
          const truncated = week && typeof week === "object" && week._truncated
          return (
            <div key={i} className="rounded border">
              <div className="bg-muted/50 px-2 py-1 text-xs font-medium">
                Week {i + 1} · {o.emphasis || "Training Week"} · {o.day_count} days
                {o.replace_from ? ` · replace_from ${o.replace_from}` : " · appended"}
              </div>
              <div className="px-2 pb-1.5">
                {truncated ? (
                  <JsonBlock value={week.preview} />
                ) : week && typeof week === "object" ? (
                  Object.entries(week)
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([date, day]) => <DayRow key={date} date={date} day={day as AnyObj} />)
                ) : (
                  <JsonBlock value={week} />
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ConfigPanel({ trace }: { trace: AnyObj[] }) {
  const llms = (trace || []).filter((e) => e.kind === "llm_turn" && e.config)
  if (!llms.length) return null
  const configs = new Map<string, AnyObj>()
  llms.forEach((e) => configs.set(cfgStr(e.config), e.config))
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-sm font-semibold mb-1">Model configuration</div>
      <div className="space-y-1">
        {[...configs.values()].map((c, i) => (
          <div key={i} className="text-xs font-mono flex flex-wrap gap-x-3">
            <span><span className="text-muted-foreground">model</span> {c.model}</span>
            <span><span className="text-muted-foreground">thinking</span> {c.thinking === "disabled" ? "disabled" : `${c.thinking_budget?.toLocaleString()} tok`}</span>
            <span><span className="text-muted-foreground">max_tokens</span> {c.max_tokens?.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function LatencyBreakdown({ run }: { run: AnyObj }) {
  const trace = run.trace || []
  const llms = trace.filter((e: AnyObj) => e.kind === "llm_turn")
  const tools = trace.filter((e: AnyObj) => e.kind === "tool")
  if (!llms.length && !tools.length) return null
  const llmMs = llms.reduce((s: number, e: AnyObj) => s + (e.duration_ms || 0), 0)
  const toolMs = tools.reduce((s: number, e: AnyObj) => s + (e.duration_ms || 0), 0)
  const wallMs = (run.duration_s || 0) * 1000
  const otherMs = Math.max(0, wallMs - llmMs - toolMs)
  const longest = llms.reduce((m: AnyObj | null, e: AnyObj) => (!m || (e.duration_ms || 0) > (m.duration_ms || 0) ? e : m), null)
  const seg = (label: string, ms: number, color: string) => {
    const pct = wallMs > 0 ? (ms / wallMs) * 100 : 0
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
        <div className="flex-1 bg-muted rounded h-3 overflow-hidden">
          <div className={cn("h-full", color)} style={{ width: `${Math.max(1, pct)}%` }} />
        </div>
        <span className="w-28 shrink-0 text-right font-mono">{fmtMs(ms)} ({pct.toFixed(0)}%)</span>
      </div>
    )
  }
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-sm font-semibold mb-2">Latency breakdown — where the {fmtMs(wallMs)} went</div>
      <div className="space-y-1.5">
        {seg("LLM turns", llmMs, "bg-sky-500")}
        {seg("Tool calls", toolMs, "bg-violet-500")}
        {seg("Polling / save / stalls", otherMs, "bg-amber-500")}
      </div>
      {longest && (longest.duration_ms || 0) > 30000 && (
        <p className="mt-2 text-xs text-rose-600">
          ⚠ Single slowest turn: LLM turn {longest.iteration} = {fmtMs(longest.duration_ms)}
          {longest.config ? ` (${cfgStr(longest.config)})` : ""} — the dominant cost.
        </p>
      )}
      <p className="mt-1 text-[11px] text-muted-foreground">
        {llms.length} LLM turns, {tools.length} tool calls. "Polling/save/stalls" includes the harness's status polling
        interval, the season save, the reviewer pass, and any single-worker stalls.
      </p>
    </div>
  )
}

function Mc5Panel({ evalData }: { evalData: AnyObj | undefined }) {
  if (!evalData) return null
  const score = evalData.overall_alignment
  const color = score == null ? "text-muted-foreground" : score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-600" : "text-rose-600"
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold">Magna Carta V / philosophy alignment</div>
        <div className={cn("text-2xl font-bold", color)}>{score != null ? `${score}/100` : "—"}</div>
      </div>
      {evalData.verdict && <p className="text-sm mb-2">{evalData.verdict}</p>}
      {evalData.parse_error ? (
        <JsonBlock value={evalData.raw || evalData.parse_error} />
      ) : (
        <>
          <div className="space-y-1 mb-2">
            {(evalData.dimensions || []).map((d: AnyObj, i: number) => (
              <div key={i} className="text-xs">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded h-3 overflow-hidden">
                    <div
                      className={cn("h-full", (d.score || 0) >= 80 ? "bg-emerald-500" : (d.score || 0) >= 60 ? "bg-amber-500" : "bg-rose-500")}
                      style={{ width: `${d.score || 0}%` }}
                    />
                  </div>
                  <span className="w-10 text-right font-mono">{d.score}</span>
                  <span className="w-44 shrink-0 truncate font-medium">{d.name}</span>
                </div>
                <div className="text-muted-foreground ml-0 mt-0.5">{d.assessment}</div>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-2 text-xs">
            {evalData.strengths?.length > 0 && (
              <div>
                <div className="font-semibold text-emerald-600">Strengths</div>
                <ul className="list-disc ml-4">{evalData.strengths.map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
            {evalData.concerns?.length > 0 && (
              <div>
                <div className="font-semibold text-rose-600">Concerns</div>
                <ul className="list-disc ml-4">{evalData.concerns.map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
          </div>
        </>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Judge: {evalData.judge_model} · {evalData.weeks_evaluated} week(s) evaluated</p>
    </div>
  )
}

function ReasoningPanel({ trace }: { trace: AnyObj[] }) {
  const gos = (trace || []).filter((e) => e.kind === "generation_output" && (e.rationale || e.diagnosis))
  if (!gos.length) return null
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-sm font-semibold mb-2">Agent reasoning (rationale + diagnosis)</div>
      <div className="space-y-2">
        {gos.map((e, i) => {
          const d = e.diagnosis || {}
          return (
            <div key={i} className="text-xs border-t pt-2 first:border-t-0 first:pt-0">
              <div className="font-mono text-muted-foreground">
                {(e.dates || [])[0]} → {(e.dates || []).slice(-1)[0]} · {e.emphasis}
              </div>
              {d.posture && (
                <div>
                  <span className="font-semibold">posture:</span> {d.posture}
                  {d.primary_driver ? ` · driver ${d.primary_driver}` : ""}
                  {d.confidence ? ` · ${d.confidence}` : ""}
                </div>
              )}
              {d.cited_inputs && <div className="text-muted-foreground">cited: {(d.cited_inputs || []).join(", ")}</div>}
              {e.rationale && <p className="mt-1 whitespace-pre-wrap">{e.rationale}</p>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SimSeasonView({ run }: { run: AnyObj }) {
  const weeks: AnyObj[] = run._sim_weeks
  if (!weeks?.length) return null
  const lifts: Record<string, (number | null)[]> = {}
  weeks.forEach((w, wi) => {
    ;(w.summary?.days || []).forEach((d: AnyObj) =>
      (d.exercises || []).forEach((ex: AnyObj) => {
        if (ex.weight == null) return
        lifts[ex.exercise] = lifts[ex.exercise] || Array(weeks.length).fill(null)
        lifts[ex.exercise][wi] = ex.weight
      })
    )
  })
  const top = Object.entries(lifts).filter(([, v]) => v.filter((x) => x != null).length >= 2).slice(0, 14)
  return (
    <div className="rounded-lg border bg-card p-3 space-y-3">
      <div className="text-sm font-semibold">
        Season simulation — {weeks.length} weeks (each prior week's completion simulated before the next)
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs w-full">
          <thead>
            <tr className="text-left border-b text-muted-foreground">
              <th className="py-1 pr-3">week</th><th className="pr-3">emphasis</th><th className="pr-3">posture</th>
              <th className="pr-3">coach notes</th><th>days</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.week} className="border-b">
                <td className="py-1 pr-3 font-mono">{w.week}</td>
                <td className="pr-3">{w.summary?.emphasis || "—"}</td>
                <td className="pr-3">{w.summary?.posture || "—"}</td>
                <td className="pr-3">{w.summary?.has_coaching_notes ? "✓" : "—"}</td>
                <td>{(w.summary?.days || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {top.length > 0 && (
        <div>
          <div className="text-xs font-semibold mb-1">Working load across weeks (progression)</div>
          <div className="overflow-x-auto">
            <table className="text-[11px] w-full">
              <thead>
                <tr className="text-left border-b text-muted-foreground">
                  <th className="py-1 pr-2">lift</th>
                  {weeks.map((w) => <th key={w.week} className="pr-2">w{w.week}</th>)}
                </tr>
              </thead>
              <tbody>
                {top.map(([name, vals]) => (
                  <tr key={name} className="border-b">
                    <td className="py-1 pr-2">{name}</td>
                    {vals.map((v, i) => <td key={i} className="pr-2 font-mono">{v ?? "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function GoalHorizonView({ run }: { run: AnyObj }) {
  const hs: AnyObj[] = run._horizons
  if (!hs?.length) return null
  return (
    <div className="rounded-lg border bg-card p-3 space-y-2">
      <div className="text-sm font-semibold">
        Goal-horizon sweep (steering ON) — how the season responds to deadline distance
      </div>
      <table className="text-xs w-full">
        <thead>
          <tr className="text-left border-b text-muted-foreground">
            <th className="py-1 pr-3">horizon</th><th className="pr-3">target</th><th className="pr-3">posture</th>
            <th className="pr-3">emphasis</th><th>cited inputs</th>
          </tr>
        </thead>
        <tbody>
          {hs.map((h) => (
            <tr key={h.horizon_weeks} className="border-b align-top">
              <td className="py-1 pr-3 font-mono">{h.horizon_weeks}w</td>
              <td className="pr-3 font-mono">{h.target_date}</td>
              <td className="pr-3 font-semibold">{h.summary?.posture || "—"}</td>
              <td className="pr-3">{h.summary?.emphasis || "—"}</td>
              <td className="text-muted-foreground">{(h.summary?.cited_inputs || []).join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RunDetail({ run }: { run: AnyObj }) {
  const s = run.summary || {}
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn("rounded border px-1.5 py-0.5 text-xs font-semibold", LEVEL_COLOR[run.level])}>{run.level}</span>
          <span className="rounded border bg-muted px-1.5 py-0.5 text-xs">{FLOW_LABEL[run.flow] || run.flow}</span>
          {run.budget && (
            <span className={cn(
              "rounded border px-1.5 py-0.5 text-xs font-mono",
              run.budget === "8k" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"
            )}>
              {run.budget} thinking
            </span>
          )}
          <h2 className="text-lg font-semibold">{run.scenario_id} — {run.title}</h2>
          {run.ok ? (
            <CheckCircle2 className="size-4 text-emerald-600" />
          ) : (
            <XCircle className="size-4 text-rose-600" />
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground font-mono">{run.intent}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi title="Wall time" value={fmtSecs(run.duration_s)} icon={Clock} />
        <Kpi title="Tool calls" value={s.tool_calls ?? 0} icon={Wrench} sub={(s.tool_names || []).join(", ") || "none"} />
        <Kpi title="LLM turns" value={s.llm_turns ?? 0} icon={Cpu} />
        <Kpi
          title="Tokens in/out"
          value={`${fmtTok(s.total_input_tokens)}/${fmtTok(s.total_output_tokens)}`}
          icon={Zap}
          sub={s.cache_read_tokens ? `cache-read ${fmtTok(s.cache_read_tokens)}` : undefined}
        />
      </div>

      <Section title="Expected vs observed">
        <p className="text-sm text-muted-foreground">{run.expect}</p>
        {run.notes && (
          <>
            <div className="text-sm font-semibold mt-2 mb-1">Observed / analysis</div>
            <p className="text-sm whitespace-pre-wrap">{run.notes}</p>
          </>
        )}
        {run.error && <p className="mt-1 text-sm text-rose-600">error: {run.error}</p>}
      </Section>

      {(run.trace || []).some((e: AnyObj) => e.kind === "llm_turn") && (
        <div className="grid md:grid-cols-2 gap-4">
          <ConfigPanel trace={run.trace} />
          <LatencyBreakdown run={run} />
        </div>
      )}

      <GeneratedOutput trace={run.trace} />

      <Mc5Panel evalData={run.mc5_eval} />

      <ReasoningPanel trace={run.trace} />

      <SimSeasonView run={run} />

      <GoalHorizonView run={run} />

      <Section title="HTTP calls" subtitle={`${(run.http || []).length} request(s)`}>
        <div className="space-y-1">
          {(run.http || []).map((h: AnyObj, i: number) => (
            <Collapse
              key={i}
              title={
                <span className="flex items-center gap-2 font-mono text-xs">
                  <StatusBadge status={h.status} />
                  <span className="font-semibold">{h.method}</span>
                  <span>{h.path}</span>
                  <span className="text-muted-foreground">
                    {h.duration_ms != null ? fmtMs(h.duration_ms) : ""}
                    {h.poll_count != null ? ` · ${h.poll_count} polls / ${h.waited_s}s → ${h.terminal_status}` : ""}
                  </span>
                </span>
              }
            >
              <div className="space-y-1">
                {h.request && (
                  <>
                    <div className="text-xs text-muted-foreground">request:</div>
                    <JsonBlock value={h.request} />
                  </>
                )}
                <div className="text-xs text-muted-foreground">response:</div>
                <JsonBlock value={h.response} />
              </div>
            </Collapse>
          ))}
        </div>
      </Section>

      <Section
        title="Agent trace (LLM turns + tool calls)"
        subtitle={`${(run.trace || []).filter((e: AnyObj) => e.kind !== "request").length} event(s)`}
      >
        <TraceTimeline trace={run.trace} />
      </Section>
    </div>
  )
}

function Bar({ label, value, max, suffix, color = "bg-sky-500" }: { label: string; value: number; max: number; suffix?: string; color?: string }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-14 shrink-0 font-mono text-muted-foreground">{label}</span>
      <div className="flex-1 bg-muted rounded h-4 overflow-hidden">
        <div className={cn("h-full rounded", color)} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-20 shrink-0 text-right font-mono">{value.toLocaleString()}{suffix || ""}</span>
    </div>
  )
}

function BiggestTimeTaker() {
  const turns: AnyObj[] = []
  RUNS.forEach((r) =>
    (r.trace || []).forEach((e: AnyObj) => {
      if (e.kind === "llm_turn") turns.push({ ...e, run: r.scenario_id })
    })
  )
  if (!turns.length) return null
  const totalMs = turns.reduce((s, e) => s + (e.duration_ms || 0), 0)
  const emitTurns = turns.filter((e) => (e.duration_ms || 0) > 60000)
  const emitMs = emitTurns.reduce((s, e) => s + (e.duration_ms || 0), 0)
  const slowest = turns.reduce((m: AnyObj | null, e) => ((e.duration_ms || 0) > (m?.duration_ms || 0) ? e : m), null)
  const toolMs = RUNS.reduce(
    (s, r) => s + (r.trace || []).filter((e: AnyObj) => e.kind === "tool").reduce((t: number, e: AnyObj) => t + (e.duration_ms || 0), 0),
    0
  )
  return (
    <Section title="Biggest time-taker" subtitle="aggregated across all captured runs">
      <p className="text-sm">
        <span className="font-semibold text-rose-600">
          Full-week emit turns (&gt;60s) account for {totalMs > 0 ? Math.round((emitMs / totalMs) * 100) : 0}% of all LLM time
        </span>{" "}
        ({(emitMs / 1000 / 60).toFixed(1)} of {(totalMs / 1000 / 60).toFixed(1)} min across {emitTurns.length} of {turns.length} turns).
        Tool calls total just {(toolMs / 1000).toFixed(1)}s — the latency lever is the emit, not the tools.
        {slowest && (
          <>
            {" "}Slowest single turn: <span className="font-mono">{fmtMs(slowest.duration_ms)}</span> in {slowest.run}
            {slowest.config ? ` (${cfgStr(slowest.config)})` : ""}.
          </>
        )}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Structural fixes shipped 2026-06-09: two-week single emit (one input/thinking/tool pass per regen instead of two),
        trimmed preload library, and live progress stages so the wait narrates itself. Compare pre/post runs in the table below.
      </p>
    </Section>
  )
}

function Analytics() {
  const byDur = [...RUNS].sort((a, b) => (b.duration_s || 0) - (a.duration_s || 0))
  const maxDur = Math.max(...RUNS.map((r) => r.duration_s || 0), 1)
  const maxTok = Math.max(...RUNS.map((r) => (r.summary?.total_input_tokens || 0) + (r.summary?.total_output_tokens || 0)), 1)

  const toolFreq: Record<string, number> = {}
  RUNS.forEach((r) => (r.summary?.tool_names || []).forEach((t: string) => (toolFreq[t] = (toolFreq[t] || 0) + 1)))
  const tools = Object.entries(toolFreq).sort((a, b) => b[1] - a[1])
  const maxTool = Math.max(...Object.values(toolFreq), 1)

  const flows: Record<string, { runs: number; dur: number; tin: number; tout: number; tools: number }> = {}
  RUNS.forEach((r) => {
    const f = (flows[r.flow] = flows[r.flow] || { runs: 0, dur: 0, tin: 0, tout: 0, tools: 0 })
    f.runs++
    f.dur += r.duration_s || 0
    f.tin += r.summary?.total_input_tokens || 0
    f.tout += r.summary?.total_output_tokens || 0
    f.tools += r.summary?.tool_calls || 0
  })

  return (
    <div className="space-y-6">
      <BiggestTimeTaker />
      <div className="rounded-lg border bg-card p-4">
        <div className="text-sm font-semibold mb-3">Wall time per run (slowest first)</div>
        <div className="space-y-1.5">
          {byDur.map((r) => (
            <Bar
              key={r.scenario_id}
              label={r.scenario_id}
              value={Math.round((r.duration_s || 0) * 10) / 10}
              max={maxDur}
              suffix="s"
              color={(r.duration_s || 0) > 60 ? "bg-rose-500" : (r.duration_s || 0) > 10 ? "bg-amber-500" : "bg-emerald-500"}
            />
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm font-semibold mb-3">Total tokens per run (in + out)</div>
          <div className="space-y-1.5">
            {[...RUNS]
              .sort(
                (a, b) =>
                  (b.summary?.total_input_tokens || 0) +
                  (b.summary?.total_output_tokens || 0) -
                  ((a.summary?.total_input_tokens || 0) + (a.summary?.total_output_tokens || 0))
              )
              .filter((r) => (r.summary?.total_input_tokens || 0) + (r.summary?.total_output_tokens || 0) > 0)
              .map((r) => (
                <Bar
                  key={r.scenario_id}
                  label={r.scenario_id}
                  value={(r.summary?.total_input_tokens || 0) + (r.summary?.total_output_tokens || 0)}
                  max={maxTok}
                  color="bg-violet-500"
                />
              ))}
          </div>
        </div>

        <div className="rounded-lg border bg-card p-4">
          <div className="text-sm font-semibold mb-3">Tool-call frequency (across all runs)</div>
          <div className="space-y-1.5">
            {tools.map(([name, n]) => (
              <Bar key={name} label={name.length > 14 ? name.slice(0, 13) + "…" : name} value={n} max={maxTool} color="bg-sky-500" />
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-4 overflow-x-auto">
        <div className="text-sm font-semibold mb-3">Per-flow rollup</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="py-1 pr-4">Flow</th>
              <th className="py-1 pr-4">Runs</th>
              <th className="py-1 pr-4">Total wall</th>
              <th className="py-1 pr-4">Tool calls</th>
              <th className="py-1 pr-4">Tokens in</th>
              <th className="py-1 pr-4">Tokens out</th>
            </tr>
          </thead>
          <tbody>
            {FLOW_ORDER.filter((f) => flows[f]).map((f) => (
              <tr key={f} className="border-b last:border-0">
                <td className="py-1 pr-4 font-medium">{FLOW_LABEL[f]}</td>
                <td className="py-1 pr-4 font-mono">{flows[f].runs}</td>
                <td className="py-1 pr-4 font-mono">{fmtSecs(flows[f].dur)}</td>
                <td className="py-1 pr-4 font-mono">{flows[f].tools}</td>
                <td className="py-1 pr-4 font-mono">{fmtTok(flows[f].tin)}</td>
                <td className="py-1 pr-4 font-mono">{fmtTok(flows[f].tout)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function weekDaysFromRun(run: AnyObj) {
  const out: AnyObj[] = []
  ;(run.trace || [])
    .filter((e: AnyObj) => e.kind === "generation_output")
    .forEach((e: AnyObj) => {
      const week = e.week
      if (week && typeof week === "object" && !week._truncated) {
        Object.entries(week).forEach(([date, day]: [string, any]) => {
          const names: string[] = []
          const sessions = day.sessions || (day.exercises ? [{ exercises: day.exercises }] : [])
          sessions.forEach((s: AnyObj) => (s.exercises || []).forEach((ex: AnyObj) => names.push(ex.exercise)))
          out.push({ date, day_name: day.day_name, names, rest: day.rest_day || !names.length })
        })
      }
    })
  return out.sort((a, b) => a.date.localeCompare(b.date))
}

function diffChangesFromRun(run: AnyObj) {
  for (const h of run.http || []) {
    const edits = h.response?.edits
    if (edits && edits[0]) {
      const d = edits[0].diff || edits[0]
      if (d.changes) return d.changes
    }
  }
  return []
}

function CompareGroup({ runs }: { runs: AnyObj[] }) {
  const isGen = runs.some((r) => (r.trace || []).some((e: AnyObj) => e.kind === "generation_output"))
  const metric = (label: string, fn: (r: AnyObj) => any) => (
    <tr className="border-b last:border-0">
      <td className="py-1 pr-3 text-muted-foreground">{label}</td>
      {runs.map((r) => (
        <td key={r.scenario_id} className="py-1 pr-3 font-mono">{fn(r)}</td>
      ))}
    </tr>
  )
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="text-sm font-semibold">
        Consistency: {runs.map((r) => r.scenario_id).join(" vs ")} <span className="text-muted-foreground">({runs[0].title})</span>
      </div>
      <table className="text-xs w-full">
        <thead>
          <tr className="text-left border-b">
            <th className="py-1 pr-3">metric</th>
            {runs.map((r) => <th key={r.scenario_id} className="py-1 pr-3">{r.scenario_id}</th>)}
          </tr>
        </thead>
        <tbody>
          {metric("wall time", (r) => fmtSecs(r.duration_s))}
          {metric("LLM turns", (r) => r.summary?.llm_turns ?? 0)}
          {metric("tool calls", (r) => r.summary?.tool_calls ?? 0)}
          {metric("tokens in/out", (r) => `${fmtTok(r.summary?.total_input_tokens)}/${fmtTok(r.summary?.total_output_tokens)}`)}
          {isGen && metric("MC5 alignment", (r) => (r.mc5_eval?.overall_alignment != null ? `${r.mc5_eval.overall_alignment}/100` : "—"))}
          {!isGen && metric("diff changes", (r) => diffChangesFromRun(r).length)}
        </tbody>
      </table>

      {isGen ? (
        (() => {
          const a = weekDaysFromRun(runs[0])
          const b = weekDaysFromRun(runs[1])
          const dates = Array.from(new Set([...a.map((d) => d.date), ...b.map((d) => d.date)])).sort()
          const byDate = (arr: AnyObj[], dt: string) => arr.find((d) => d.date === dt)
          return (
            <div>
              <div className="text-xs font-semibold mb-1">Per-day exercise comparison (eyeball run-to-run drift)</div>
              <div className="overflow-x-auto">
                <table className="text-[11px] w-full">
                  <thead>
                    <tr className="text-left border-b text-muted-foreground">
                      <th className="py-1 pr-2">date</th>
                      <th className="py-1 pr-2">{runs[0].scenario_id}</th>
                      <th className="py-1 pr-2">{runs[1].scenario_id}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dates.map((dt) => {
                      const da = byDate(a, dt)
                      const db = byDate(b, dt)
                      const same = JSON.stringify(da?.names || []) === JSON.stringify(db?.names || [])
                      return (
                        <tr key={dt} className={cn("border-b align-top", !same && "bg-amber-500/5")}>
                          <td className="py-1 pr-2 font-mono whitespace-nowrap">
                            {dt.slice(5)} {da?.day_name?.slice(0, 3) || db?.day_name?.slice(0, 3)}
                          </td>
                          <td className="py-1 pr-2">{da ? (da.rest ? "Rest" : da.names.join(", ")) : "—"}</td>
                          <td className="py-1 pr-2">{db ? (db.rest ? "Rest" : db.names.join(", ")) : "—"}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Amber rows = the two runs produced different exercises for that day.</p>
            </div>
          )
        })()
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {runs.map((r) => {
            const ch = diffChangesFromRun(r)
            return (
              <div key={r.scenario_id} className="text-xs">
                <div className="font-semibold mb-1">{r.scenario_id} — {ch.length} change(s)</div>
                <ul className="space-y-0.5">
                  {ch.slice(0, 30).map((c: AnyObj, i: number) => (
                    <li key={i} className="font-mono">
                      <span className="text-muted-foreground">{(c.day || "").slice(5)} {c.type}</span>{" "}
                      {c.field}: {JSON.stringify(c.old_value)?.slice(0, 30)} → {JSON.stringify(c.new_value)?.slice(0, 40)}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Compare() {
  const groups: Record<string, AnyObj[]> = {}
  RUNS.forEach((r) => {
    if (r.group) (groups[r.group] = groups[r.group] || []).push(r)
  })
  const entries = Object.entries(groups).filter(([, rs]) => rs.length >= 2)
  if (!entries.length)
    return <p className="text-sm text-muted-foreground">No A/B comparison groups captured yet.</p>
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The same workflow run twice, to gauge run-to-run consistency of the agent (same prompt, same account, different
        completion). Generation groups compare the produced weeks day by day; edit groups compare the diffs.
      </p>
      {entries.map(([g, rs]) => (
        <CompareGroup key={g} runs={rs.slice(0, 2)} />
      ))}
    </div>
  )
}

// Frozen snapshot: sprint status cross-checked against the flow audit. Static
// rows, not derived from audit-data.json.
const GOAL_STORY_ROWS: Array<{ story: string; status: string; evidence: string }> = [
  { story: "Epic 1 — Goal & target-date management (11 stories)", status: "done", evidence: "GO1-GO3 runs: /me/goals CRUD live; event-goal-requires-date 422; ISO validation 400. Goal-horizon sweep created/deleted dated event goals through the live API." },
  { story: "Epic 2 — Diagnosis engine, shadow (9 stories)", status: "done", evidence: "Every generation run carries a diagnosis block (posture/cited_inputs) in generation_output; get_progress_snapshot composes goals + weeks-to-target (visible in G3/SIM tool traces)." },
  { story: "Epic 3 — Observability & flip readiness (5 stories)", status: "done", evidence: "Per-user steering knob (profiles.agent_overrides.diagnosis_steering) flipped on/off live by the GOALH harness; admin digest exists." },
  { story: "4.1 Distance-to-target phase selection", status: "done", evidence: "GOALH (steering ON): posture=taper at 4-week horizon, posture=progress at 8-10 weeks — periodization follows deadline distance." },
  { story: "4.2 Coach-voice rationale reveal", status: "done", evidence: "Generation rationale names the goal and weeks-to-target (Reasoning panel on generation runs)." },
  { story: "4.3 Unrealistic-deadline copy + 4.4 chat grounding", status: "done", evidence: "Verified in the 2026-06-05 live E2E (memory: feasibility=realistic flows; get_latest_diagnosis humanized)." },
  { story: "4.7 Goal-driven discipline coverage", status: "done", evidence: "Live-verified 2026-06-05: triathlon goal injects swim/bike into a schedule that omitted them (steering-gated)." },
  { story: "4.5 Fleet flip + 4.6 prescription-rules-to-prompt", status: "backlog (held by design)", evidence: "Steering stays default-OFF in code; the per-user knob is the rollout path and a fleet flip is a code change. Deliberately deferred, not missing." },
]

function GoalStoryVerification() {
  return (
    <Section title="Goal event-target story — verification against the flow audit" subtitle="sprint-status × live evidence">
      <p className="text-xs text-muted-foreground mb-2">
        Verdict: the goal event-target feature is COMPLETE and live-verified. Users can set a dated event goal; the agent
        perceives it (progress snapshot), steers periodization by distance-to-target when steering is on (per-user knob),
        and explains the goal in its rationale. Only the deliberate holds (4.5 fleet flip, 4.6) remain in backlog.
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground border-b">
            <th className="py-1 pr-3">Story</th>
            <th className="py-1 pr-3">Sprint status</th>
            <th className="py-1">Flow-audit / live evidence</th>
          </tr>
        </thead>
        <tbody>
          {GOAL_STORY_ROWS.map((r) => (
            <tr key={r.story} className="border-b last:border-0 align-top">
              <td className="py-1 pr-3 font-medium">{r.story}</td>
              <td className={cn("py-1 pr-3 font-mono whitespace-nowrap", r.status === "done" ? "text-emerald-600" : "text-amber-600")}>{r.status}</td>
              <td className="py-1 text-muted-foreground">{r.evidence}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  )
}

const STATUS_ORDER = ["open", "improved", "resolved", "verified", "info"]

function Findings() {
  const [filter, setFilter] = useState<string>("all")
  if (!FINDINGS.length) return <p className="text-sm text-muted-foreground">No findings recorded.</p>

  const counts: Record<string, number> = {}
  for (const f of FINDINGS) {
    const s = (f.status as string) || "open"
    counts[s] = (counts[s] || 0) + 1
  }
  const statuses = STATUS_ORDER.filter((s) => counts[s])
  const isOpen = (f: AnyObj) => ((f.status as string) || "open") === "open"
  // Open items first, then improved, then the rest; severity within each group.
  const SEV_RANK: Record<string, number> = { CRITICAL: 0, MAJOR: 1, MINOR: 2, INFO: 3 }
  const shown = FINDINGS
    .filter((f) => filter === "all" || ((f.status as string) || "open") === filter)
    .slice()
    .sort((a, b) => {
      const sa = STATUS_ORDER.indexOf((a.status as string) || "open")
      const sb = STATUS_ORDER.indexOf((b.status as string) || "open")
      if (sa !== sb) return sa - sb
      return (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9)
    })

  return (
    <div className="space-y-3">
      <GoalStoryVerification />
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => setFilter("all")}
          className={cn("rounded border px-2 py-0.5 text-xs font-semibold",
            filter === "all" ? "bg-foreground text-background" : "bg-muted text-muted-foreground")}
        >
          All {FINDINGS.length}
        </button>
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(filter === s ? "all" : s)}
            className={cn("rounded border px-2 py-0.5 text-xs font-semibold",
              filter === s ? STATUS_COLOR[s] : "bg-muted text-muted-foreground")}
          >
            {STATUS_LABEL[s] || s.toUpperCase()} {counts[s]}
          </button>
        ))}
      </div>
      {shown.map((f, i) => (
        <div key={i} className={cn("rounded-lg border bg-card p-4", !isOpen(f) && "opacity-80")}>
          <div className="flex items-center gap-2 flex-wrap">
            {f.status && (
              <span className={cn("rounded border px-1.5 py-0.5 text-xs font-semibold", STATUS_COLOR[f.status] || STATUS_COLOR.open)}>
                {STATUS_LABEL[f.status] || String(f.status).toUpperCase()}
              </span>
            )}
            <span className={cn("rounded border px-1.5 py-0.5 text-xs font-semibold", SEV_COLOR[f.severity] || SEV_COLOR.INFO)}>
              {f.severity}
            </span>
            {f.scenario && <span className="rounded border bg-muted px-1.5 py-0.5 text-xs font-mono">{f.scenario}</span>}
            <h3 className={cn("font-semibold", !isOpen(f) && "line-through decoration-1 decoration-muted-foreground/50")}>{f.title}</h3>
          </div>
          {f.resolution && (
            <div className="mt-2 rounded border border-emerald-500/30 bg-emerald-500/5 px-2 py-1.5">
              <div className="text-xs font-semibold text-emerald-600">Resolution</div>
              <p className="text-sm whitespace-pre-wrap">{f.resolution}</p>
            </div>
          )}
          {f.evidence && (
            <div className="mt-2">
              <div className="text-xs font-semibold text-muted-foreground">Evidence</div>
              <p className="text-sm whitespace-pre-wrap">{f.evidence}</p>
            </div>
          )}
          {f.recommendation && (
            <div className="mt-2">
              <div className="text-xs font-semibold text-muted-foreground">Recommendation</div>
              <p className="text-sm whitespace-pre-wrap">{f.recommendation}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// Thinking-budget sweep runs: ids end in _b<budget> (generation) or _eb<budget> (edit).
const BUDGET_RUN = /_(b|eb)(\d+)$/

function budgetOf(id: string): number {
  const m = id.match(BUDGET_RUN)
  return m ? parseInt(m[2], 10) : 0
}

function mc5Overall(r: AnyObj): number | null {
  return r.mc5_eval?.overall_alignment ?? null
}

function BudgetSweep() {
  const sweepRuns = RUNS.filter((r) => BUDGET_RUN.test(r.scenario_id))
  const genRuns = sweepRuns
    .filter((r) => r.scenario_id.includes("_b") && !r.scenario_id.includes("_eb"))
    .sort((a, b) => budgetOf(b.scenario_id) - budgetOf(a.scenario_id))
  const editRuns = sweepRuns
    .filter((r) => r.scenario_id.includes("_eb"))
    .sort((a, b) => budgetOf(b.scenario_id) - budgetOf(a.scenario_id))

  if (sweepRuns.length === 0) {
    return <p className="text-sm text-muted-foreground">No budget-sweep runs in this snapshot.</p>
  }

  const Table = ({ rows, kind }: { rows: AnyObj[]; kind: string }) => (
    <div className="rounded-lg border">
      <div className="border-b bg-muted/30 px-3 py-2 text-sm font-semibold">{kind}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase text-muted-foreground">
            <th className="px-3 py-2">Thinking budget</th>
            <th className="px-3 py-2">Wall time</th>
            <th className="px-3 py-2">Tokens in/out</th>
            <th className="px-3 py-2">MC5</th>
            <th className="px-3 py-2">Weakest dimension</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const dims: AnyObj[] = r.mc5_eval?.dimensions || []
            const weakest = dims.length
              ? dims.reduce((a, b) => ((a.score ?? 101) <= (b.score ?? 101) ? a : b))
              : null
            const locked = budgetOf(r.scenario_id) === 8000
            return (
              <tr key={r.scenario_id} className={cn("border-b last:border-0", locked && "bg-emerald-500/5")}>
                <td className="px-3 py-2 font-mono">
                  {(budgetOf(r.scenario_id) / 1000).toFixed(0)}k
                  {locked && <span className="ml-2 rounded bg-emerald-500/15 px-1.5 text-[10px] font-semibold text-emerald-600">LOCKED IN</span>}
                </td>
                <td className="px-3 py-2 font-mono">{fmtSecs(r.duration_s)}</td>
                <td className="px-3 py-2 font-mono">
                  {fmtTok(r.summary?.total_input_tokens || 0)}/{fmtTok(r.summary?.total_output_tokens || 0)}
                </td>
                <td className="px-3 py-2 font-mono font-semibold">{mc5Overall(r) ?? "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {weakest ? `${weakest.name} (${weakest.score})` : "—"}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  // Per-flow 32k vs 8k pairs, matched by `<ID>` / `<ID>_8k`.
  const pairs = RUNS.filter((r) => r.scenario_id.endsWith("_8k"))
    .map((r8) => ({
      id: r8.scenario_id.replace(/_8k$/, ""),
      r32: RUNS.find((r) => r.scenario_id === r8.scenario_id.replace(/_8k$/, "")),
      r8,
    }))
    .filter((p) => p.r32)

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/20 p-3 text-sm">
        <p className="font-semibold">Thinking budget vs quality (2026-06-10 experiment)</p>
        <p className="mt-1 text-muted-foreground">
          MC5 reasoning scores were budget-INSENSITIVE across 4k–32k (78–84, judge noise ~±3) while
          thinking spend scales linearly; wall time is dominated by the generation PATH (combined emit
          vs fallback vs corrections), not the budget. Locked in: 8k for both generation and edit
          (settings pin-tested). One run per cell — treat single-cell deltas as noise, not signal.
        </p>
      </div>
      {pairs.length > 0 && (
        <div className="rounded-lg border">
          <div className="border-b bg-muted/30 px-3 py-2 text-sm font-semibold">
            Flows re-verified at the locked-in 8k (vs the original 32k capture)
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">Flow</th>
                <th className="px-3 py-2">32k wall</th>
                <th className="px-3 py-2">8k wall</th>
                <th className="px-3 py-2">Δ time</th>
                <th className="px-3 py-2">32k MC5</th>
                <th className="px-3 py-2">8k MC5</th>
              </tr>
            </thead>
            <tbody>
              {pairs.map(({ id, r32, r8 }) => {
                const d32 = r32!.duration_s || 0
                const d8 = r8.duration_s || 0
                const delta = d32 > 0 ? Math.round(((d8 - d32) / d32) * 100) : 0
                return (
                  <tr key={id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <span className="font-mono font-semibold">{id}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{r32!.title}</span>
                    </td>
                    <td className="px-3 py-2 font-mono">{fmtSecs(d32)}</td>
                    <td className="px-3 py-2 font-mono">{fmtSecs(d8)}</td>
                    <td className={cn("px-3 py-2 font-mono", delta < 0 ? "text-emerald-600" : delta > 10 ? "text-rose-600" : "text-muted-foreground")}>
                      {delta > 0 ? "+" : ""}{delta}%
                    </td>
                    <td className="px-3 py-2 font-mono">{mc5Overall(r32!) ?? "—"}</td>
                    <td className="px-3 py-2 font-mono font-semibold">{mc5Overall(r8) ?? "—"}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {genRuns.length > 0 && <Table rows={genRuns} kind="Budget sweep — G3 two-week regen (32k/16k/8k/4k)" />}
      {editRuns.length > 0 && <Table rows={editRuns} kind="Budget sweep — E1 propose diff (MC5 not applicable; latency comparison)" />}
      <p className="text-xs text-muted-foreground">
        Full transcripts, tool calls, and generated weeks for every run are in the Runs tab. Every run
        row carries its thinking-budget chip; unmarked legacy runs were captured at 32k.
      </p>
    </div>
  )
}

export function AuditPage() {
  const [tab, setTab] = useState<"runs" | "analytics" | "compare" | "findings" | "budget">("runs")
  const [selected, setSelected] = useState<string>(RUNS[0]?.scenario_id || "")

  const grouped = useMemo(() => {
    const g: Record<string, AnyObj[]> = {}
    RUNS.forEach((r) => (g[r.flow] = g[r.flow] || []).push(r))
    return g
  }, [])

  const totals = useMemo(() => {
    let dur = 0,
      tools = 0,
      tin = 0,
      tout = 0
    RUNS.forEach((r) => {
      dur += r.duration_s || 0
      tools += r.summary?.tool_calls || 0
      tin += r.summary?.total_input_tokens || 0
      tout += r.summary?.total_output_tokens || 0
    })
    return { dur, tools, tin, tout }
  }, [])

  const selectedRun = RUNS.find((r) => r.scenario_id === selected)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="size-6" /> Backend User-Flow Audit
        </h1>
        <p className="text-sm text-muted-foreground">
          {DATA.meta?.account} · today {DATA.meta?.today} · captured {DATA.meta?.updated_at?.slice(0, 16)?.replace("T", " ")} · one-time static snapshot
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Kpi title="Scenarios" value={RUNS.length} icon={Activity} />
        <Kpi title="Total wall time" value={fmtSecs(totals.dur)} icon={Clock} />
        <Kpi title="Tool calls" value={totals.tools} icon={Wrench} />
        <Kpi title="Tokens in/out" value={`${fmtTok(totals.tin)}/${fmtTok(totals.tout)}`} icon={Zap} />
        <Kpi title="Findings" value={FINDINGS.length} icon={AlertTriangle} />
      </div>

      <div className="flex gap-1 border-b">
        {([
          ["runs", "Runs", Database],
          ["analytics", "Analytics", Activity],
          ["compare", "Compare", GitCompare],
          ["budget", "Budget Sweep", Zap],
          ["findings", "Findings", AlertTriangle],
        ] as const).map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px",
              tab === id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <Icon className="size-4" />
            {label}
            {id === "findings" && FINDINGS.length > 0 && (
              <span className="ml-1 rounded-full bg-rose-500/15 text-rose-600 px-1.5 text-xs">{FINDINGS.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "runs" && (
        <div className="grid md:grid-cols-[260px_1fr] gap-4">
          <div className="space-y-3">
            {FLOW_ORDER.filter((f) => grouped[f]).map((f) => (
              <div key={f}>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">{FLOW_LABEL[f]}</div>
                <div className="space-y-0.5">
                  {grouped[f].map((r) => (
                    <button
                      key={r.scenario_id}
                      onClick={() => setSelected(r.scenario_id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted/50",
                        selected === r.scenario_id && "bg-muted"
                      )}
                    >
                      <span className={cn("rounded border px-1 text-[10px] font-semibold", LEVEL_COLOR[r.level])}>{r.level}</span>
                      <span className="font-mono text-xs">{r.scenario_id}</span>
                      {r.budget && (
                        <span className={cn(
                          "rounded border px-1 text-[10px] font-mono",
                          r.budget === "8k" ? "border-emerald-500/40 text-emerald-600" : "text-muted-foreground"
                        )}>
                          {r.budget}
                        </span>
                      )}
                      <span className="flex-1 truncate text-xs text-muted-foreground">{r.title}</span>
                      <span className={cn("font-mono text-[10px]", (r.duration_s || 0) > 60 ? "text-rose-600" : "text-muted-foreground")}>
                        {fmtSecs(r.duration_s)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div>{selectedRun ? <RunDetail run={selectedRun} /> : <p className="text-muted-foreground">No run selected.</p>}</div>
        </div>
      )}

      {tab === "analytics" && <Analytics />}
      {tab === "compare" && <Compare />}
      {tab === "budget" && <BudgetSweep />}
      {tab === "findings" && <Findings />}
    </div>
  )
}

export default AuditPage
